/**
 * 벽돌깨기 (Breakout) — 기존 ShoppingRush 완전 교체
 * MiniGame ID 'shoppingRush' 유지, 이름만 "🧱 벽돌깨기"
 */
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../../config';
import { Theme as T } from '../../ui/theme';
import { Button } from '../../ui/Button';
import { ToastManager } from '../../ui/ToastManager';
import { MiniGameRunner } from '../../systems/MiniGameRunner';
import { AudioManager } from '../../systems/AudioManager';

// Brick grid
const BRICK_COLS = 7;
const BRICK_ROWS = 5;
const BRICK_W = 140;
const BRICK_H = 36;
const BRICK_GAP = 8;
const BRICKS_START_X = (GAME_WIDTH - (BRICK_COLS * (BRICK_W + BRICK_GAP) - BRICK_GAP)) / 2;
const BRICKS_START_Y = 150;

// Paddle
const PADDLE_W = 180;
const PADDLE_H = 22;
const PADDLE_Y = GAME_HEIGHT - 100;

// Ball
const BALL_RADIUS = 14;
const BALL_BASE_SPEED = 520; // px/s

// Rarity colors for bricks
const BRICK_RARITY_COLORS = [
  0xaaaaaa, // row 0 - common (gray)
  0x6fd4c4, // row 1 - rare (teal)
  0x6fd4c4, // row 2 - rare
  0x9b6bff, // row 3 - epic (purple)
  0xffc94c, // row 4 - legend (gold)
];

// Brick rarity points
const BRICK_POINTS = [5, 10, 10, 20, 50];

// Lives
const MAX_LIVES = 3;

type Brick = {
  rect: Phaser.GameObjects.Rectangle;
  outline: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  row: number; col: number;
  active: boolean;
};

export class ShoppingRushScene extends Phaser.Scene {
  private score = 0;
  private lives = MAX_LIVES;
  private bricks: Brick[] = [];
  private paddle!: Phaser.GameObjects.Rectangle;
  private paddleLabel!: Phaser.GameObjects.Text;
  private ball!: Phaser.GameObjects.Arc;
  private ballVX = 0;
  private ballVY = 0;
  private gameStarted = false;
  private ended = false;
  private toast!: ToastManager;
  private scoreText!: Phaser.GameObjects.Text;
  private gameTimer!: Phaser.Time.TimerEvent;
  private livesIcons: Phaser.GameObjects.Text[] = [];

  constructor() { super('ShoppingRushScene'); }

  create(): void {
    AudioManager.setScene(this);
    AudioManager.playBgm('minigame');
    this.cameras.main.setBackgroundColor('#0d0820');
    this.toast = new ToastManager(this);
    this.score = 0; this.lives = MAX_LIVES; this.ended = false; this.gameStarted = false;
    this.bricks = [];

    // ── Header ──────────────────────────────────────────────────────────────
    this.add.text(GAME_WIDTH / 2, 55, '🧱 벽돌깨기', {
      fontFamily: T.font.family, fontSize: '34px', fontStyle: '800', color: '#ffffff',
    }).setOrigin(0.5);

    this.add.text(GAME_WIDTH / 2, 100, '모든 벽돌을 깨서 별 3개를 모으세요!', {
      fontFamily: T.font.family, fontSize: '14px', color: '#aaaacc',
    }).setOrigin(0.5);

    // Score chip
    const sG = this.add.graphics();
    sG.fillStyle(0x1a0f3a, 1); sG.fillRoundedRect(16, 12, 160, 44, 12);
    sG.lineStyle(1.5, 0x9b6bff, 1); sG.strokeRoundedRect(16, 12, 160, 44, 12);
    this.scoreText = this.add.text(96, 34, '점수: 0', {
      fontFamily: T.font.family, fontSize: '18px', fontStyle: '800', color: '#ffd95c',
    }).setOrigin(0.5);

    // Lives (hearts)
    this.livesIcons = [];
    for (let i = 0; i < MAX_LIVES; i++) {
      const h = this.add.text(GAME_WIDTH - 50 - i * 38, 34, '❤️', { fontSize: '22px' }).setOrigin(0.5);
      this.livesIcons.push(h);
    }

    // Build bricks
    this._buildBricks();

    // Paddle
    this.paddle = this.add.rectangle(GAME_WIDTH / 2, PADDLE_Y, PADDLE_W, PADDLE_H, 0xff6fa1);
    this.paddle.setStrokeStyle(3, 0xffd95c);
    this.paddleLabel = this.add.text(GAME_WIDTH / 2, PADDLE_Y, '🛹', { fontSize: '18px' }).setOrigin(0.5);

    // Ball (start on paddle)
    this.ball = this.add.arc(GAME_WIDTH / 2, PADDLE_Y - PADDLE_H / 2 - BALL_RADIUS, BALL_RADIUS, 0, 360, false, 0xffffff);
    this.ball.setStrokeStyle(3, 0xffd95c);
    this.ballVX = 0;
    this.ballVY = 0;

    // Bottom wall line
    this.add.line(0, 0, 0, GAME_HEIGHT - 60, GAME_WIDTH, GAME_HEIGHT - 60, 0xff3366, 0.4);

    // Controls
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      const px = Phaser.Math.Clamp(p.x, PADDLE_W / 2, GAME_WIDTH - PADDLE_W / 2);
      this.paddle.x = px;
      this.paddleLabel.x = px;
      if (!this.gameStarted) {
        this.ball.x = px;
      }
    });
    this.input.keyboard?.on('keydown-LEFT', () => this._movePaddle(-60));
    this.input.keyboard?.on('keydown-RIGHT', () => this._movePaddle(60));
    this.input.on('pointerdown', () => this._launch());
    this.input.keyboard?.on('keydown-SPACE', () => this._launch());

    // "탭/스페이스로 발사" hint
    const hint = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 160, '탭 또는 스페이스로 발사!', {
      fontFamily: T.font.family, fontSize: '16px', color: '#aaaacc',
    }).setOrigin(0.5);
    this.tweens.add({ targets: hint, alpha: 0, duration: 400, delay: 2000, onComplete: () => hint.destroy() });

    this.gameTimer = this.time.addEvent({ delay: 16, loop: true, callback: () => this._tick(16 / 1000) });

    new Button(this, 100, GAME_HEIGHT - 50, '← 종료', () => this._end(), { width: 120, height: 48, style: 'secondary' });
  }

  private _movePaddle(dx: number): void {
    this.paddle.x = Phaser.Math.Clamp(this.paddle.x + dx, PADDLE_W / 2, GAME_WIDTH - PADDLE_W / 2);
    this.paddleLabel.x = this.paddle.x;
    if (!this.gameStarted) this.ball.x = this.paddle.x;
  }

  private _buildBricks(): void {
    this.bricks = [];
    for (let row = 0; row < BRICK_ROWS; row++) {
      for (let col = 0; col < BRICK_COLS; col++) {
        const x = BRICKS_START_X + col * (BRICK_W + BRICK_GAP) + BRICK_W / 2;
        const y = BRICKS_START_Y + row * (BRICK_H + BRICK_GAP) + BRICK_H / 2;
        const color = BRICK_RARITY_COLORS[row];

        const rect = this.add.rectangle(x, y, BRICK_W, BRICK_H, color);
        const outline = this.add.rectangle(x, y, BRICK_W, BRICK_H);
        outline.setStrokeStyle(2, 0xffffff, 0.3);
        outline.setFillStyle(0x000000, 0);

        // Brick label (emoji based on row)
        const emojis = ['👕', '👗', '👠', '👑', '✨'];
        const label = this.add.text(x, y, emojis[row], { fontSize: '16px' }).setOrigin(0.5);

        this.bricks.push({ rect, outline, label, row, col, active: true });
      }
    }
  }

  private _launch(): void {
    if (this.gameStarted || this.ended) return;
    this.gameStarted = true;
    // Launch at a random upward angle
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.8;
    this.ballVX = Math.cos(angle) * BALL_BASE_SPEED;
    this.ballVY = Math.sin(angle) * BALL_BASE_SPEED;
  }

  private _tick(dt: number): void {
    if (!this.gameStarted || this.ended) return;

    // Move ball
    this.ball.x += this.ballVX * dt;
    this.ball.y += this.ballVY * dt;

    // Wall collisions
    if (this.ball.x - BALL_RADIUS < 0) {
      this.ball.x = BALL_RADIUS;
      this.ballVX = Math.abs(this.ballVX);
    }
    if (this.ball.x + BALL_RADIUS > GAME_WIDTH) {
      this.ball.x = GAME_WIDTH - BALL_RADIUS;
      this.ballVX = -Math.abs(this.ballVX);
    }
    if (this.ball.y - BALL_RADIUS < 80) {
      this.ball.y = 80 + BALL_RADIUS;
      this.ballVY = Math.abs(this.ballVY);
    }

    // Ball falls below paddle
    if (this.ball.y + BALL_RADIUS > GAME_HEIGHT - 55) {
      this._loseLife();
      return;
    }

    // Paddle collision
    const bLeft = this.ball.x - BALL_RADIUS;
    const bRight = this.ball.x + BALL_RADIUS;
    const bTop = this.ball.y - BALL_RADIUS;
    const bBottom = this.ball.y + BALL_RADIUS;
    const pLeft = this.paddle.x - PADDLE_W / 2;
    const pRight = this.paddle.x + PADDLE_W / 2;
    const pTop = this.paddle.y - PADDLE_H / 2;
    const pBottom = this.paddle.y + PADDLE_H / 2;

    if (bBottom >= pTop && bTop <= pBottom && bRight >= pLeft && bLeft <= pRight && this.ballVY > 0) {
      // Bounce — angle depends on where the ball hits the paddle
      const hitOffset = (this.ball.x - this.paddle.x) / (PADDLE_W / 2); // -1 to 1
      const angle = -Math.PI / 2 + hitOffset * 0.9;
      const speed = Math.sqrt(this.ballVX * this.ballVX + this.ballVY * this.ballVY);
      const boostedSpeed = Math.min(speed * 1.02, BALL_BASE_SPEED * 1.8);
      this.ballVX = Math.cos(angle) * boostedSpeed;
      this.ballVY = Math.sin(angle) * boostedSpeed;
      this.ball.y = pTop - BALL_RADIUS;
      AudioManager.playSfx('click');

      // Paddle bounce effect
      this.tweens.add({ targets: this.paddle, scaleY: 0.7, duration: 60, yoyo: true });
    }

    // Brick collisions
    for (const brick of this.bricks) {
      if (!brick.active) continue;
      const bkLeft = brick.rect.x - BRICK_W / 2;
      const bkRight = brick.rect.x + BRICK_W / 2;
      const bkTop = brick.rect.y - BRICK_H / 2;
      const bkBottom = brick.rect.y + BRICK_H / 2;

      if (bRight >= bkLeft && bLeft <= bkRight && bBottom >= bkTop && bTop <= bkBottom) {
        brick.active = false;
        brick.rect.destroy();
        brick.outline.destroy();
        brick.label.destroy();

        // Determine which side was hit for reflection
        const overlapLeft = bRight - bkLeft;
        const overlapRight = bkRight - bLeft;
        const overlapTop = bBottom - bkTop;
        const overlapBottom = bkBottom - bTop;
        const minOverlap = Math.min(overlapLeft, overlapRight, overlapTop, overlapBottom);
        if (minOverlap === overlapTop || minOverlap === overlapBottom) {
          this.ballVY = -this.ballVY;
        } else {
          this.ballVX = -this.ballVX;
        }

        // Score + visual effect
        const pts = BRICK_POINTS[brick.row];
        this.score += pts;
        this.scoreText.setText(`점수: ${this.score}`);
        AudioManager.playSfx('correct');

        // Points popup
        const px = brick.rect.x;
        const py = brick.rect.y;
        const pop = this.add.text(px, py, `+${pts}`, {
          fontFamily: T.font.family, fontSize: '20px', fontStyle: '900',
          color: brick.row >= 3 ? '#ffd95c' : '#ffffff',
        }).setOrigin(0.5).setDepth(100);
        this.tweens.add({ targets: pop, y: py - 50, alpha: 0, duration: 700, onComplete: () => pop.destroy() });

        // Occasional outfit drop sparkle (visual only)
        if (Math.random() < 0.15) {
          const spark = this.add.text(px, py, '✨', { fontSize: '28px' }).setOrigin(0.5).setDepth(100);
          this.tweens.add({ targets: spark, y: py - 80, alpha: 0, scale: 2, duration: 900, onComplete: () => spark.destroy() });
        }

        break; // one brick per frame for clean physics
      }
    }

    // Check win condition
    if (this.bricks.every(b => !b.active)) {
      this._winGame();
    }
  }

  private _loseLife(): void {
    this.lives--;
    AudioManager.playSfx('wrong');

    // Update hearts
    if (this.livesIcons[this.lives]) {
      this.livesIcons[this.lives].setText('🖤');
    }

    if (this.lives <= 0) {
      this._end();
      return;
    }

    // Reset ball to paddle
    this.gameStarted = false;
    this.ball.setPosition(this.paddle.x, PADDLE_Y - PADDLE_H / 2 - BALL_RADIUS);
    this.ballVX = 0;
    this.ballVY = 0;

    const hint = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, `❤️ ${this.lives}개 남음 — 탭으로 재발사!`, {
      fontFamily: T.font.family, fontSize: '22px', fontStyle: '800', color: '#ff6fa1',
    }).setOrigin(0.5).setDepth(200);
    this.tweens.add({ targets: hint, alpha: 0, duration: 400, delay: 1500, onComplete: () => hint.destroy() });
  }

  private _winGame(): void {
    if (this.ended) return;
    this.ended = true;
    this.gameTimer.remove();

    // Star rating based on score
    const totalPossible = BRICK_ROWS * BRICK_COLS * 20; // rough max
    const ratio = this.score / totalPossible;
    const stars = ratio >= 0.9 ? '⭐⭐⭐' : ratio >= 0.6 ? '⭐⭐' : '⭐';

    const winText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, `🎉 클리어! ${stars}`, {
      fontFamily: T.font.family, fontSize: '48px', fontStyle: '900', color: '#ffd95c',
    }).setOrigin(0.5).setDepth(300);
    this.tweens.add({ targets: winText, scale: 1.2, duration: 200, yoyo: true, repeat: 2 });

    // Bonus points for clearing
    this.score += this.lives * 50;

    this.time.delayedCall(1500, () => this._end());
  }

  private _end(): void {
    if (this.ended && !this.gameTimer.hasDispatched) return;
    this.ended = true;
    if (this.gameTimer) this.gameTimer.remove();
    const reward = MiniGameRunner.reportResult('shoppingRush', this.score);
    AudioManager.playSfx(this.score > 0 ? 'win' : 'lose');
    this.cameras.main.fadeOut(300);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.toast.show(`🧱 ${this.score}점! ⭐+${reward.stars}, 💰+${reward.coins}`, 'gold');
      this.time.delayedCall(1800, () => this.scene.start('MiniGameHubScene'));
    });
  }
}
